import React, { useState } from 'react';
import Portal from './Portal';
import {
  Settings,
  Mail,
  Shield,
  Upload,
  Zap,
  Database,
  Globe,
  Lock,
  Activity,
  FileText,
  HelpCircle,
  X
} from 'lucide-react';

// --- Reusable primitives for a consistent, minimal look ---
const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-4">
      {Icon && <Icon className="w-4 h-4 text-blue-600" />}
      {title}
    </h3>
    <div className="text-sm text-gray-600">{children}</div>
  </div>
);

const Tip = ({ title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-2">
      <HelpCircle className="w-4 h-4 text-blue-600" />
      {title}
    </h3>
    <div className="text-sm text-gray-600">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="w-1 h-1 rounded-full bg-blue-600 mt-2 shrink-0" />
    <span>{children}</span>
  </li>
);

const Sub = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{children}</h4>
);

const SettingsGuide = ({ onClose }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  const sections = [
    {
      id: 'overview',
      title: 'Settings Overview',
      icon: Settings,
      content: (
        <div className="space-y-5">
          <p className="text-gray-600">
            Configure your email verification system for optimal performance and security.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card icon={Mail} title="Email Configuration">
              <ul className="space-y-2">
                <Bullet><strong className="text-gray-800">SMTP Settings</strong> — configure email sending.</Bullet>
                <Bullet><strong className="text-gray-800">IMAP Settings</strong> — set up email receiving.</Bullet>
                <Bullet><strong className="text-gray-800">Email Scanner</strong> — customize the verification process.</Bullet>
                <Bullet><strong className="text-gray-800">Disposable Domains</strong> — manage blocked domains.</Bullet>
              </ul>
            </Card>

            <Card icon={Shield} title="Security & License">
              <ul className="space-y-2">
                <Bullet><strong className="text-gray-800">License Management</strong> — verify your license.</Bullet>
                <Bullet><strong className="text-gray-800">System Security</strong> — configure CORS and logs.</Bullet>
                <Bullet><strong className="text-gray-800">API Access</strong> — manage API tokens.</Bullet>
                <Bullet><strong className="text-gray-800">Rate Limiting</strong> — control verification speed.</Bullet>
              </ul>
            </Card>

            <Card icon={Upload} title="Upload & Processing">
              <ul className="space-y-2">
                <Bullet><strong className="text-gray-800">File Upload Limits</strong> — set size restrictions.</Bullet>
                <Bullet><strong className="text-gray-800">Concurrency Settings</strong> — control processing speed.</Bullet>
                <Bullet><strong className="text-gray-800">Worker Configuration</strong> — optimize background jobs.</Bullet>
                <Bullet><strong className="text-gray-800">Bulk Scan Settings</strong> — schedule automated scans.</Bullet>
              </ul>
            </Card>

            <Card icon={Database} title="Templates & Data">
              <ul className="space-y-2">
                <Bullet><strong className="text-gray-800">Verification Templates</strong> — custom email templates.</Bullet>
                <Bullet><strong className="text-gray-800">Error Strings</strong> — manage bounce patterns.</Bullet>
                <Bullet><strong className="text-gray-800">Role-based Emails</strong> — configure business emails.</Bullet>
                <Bullet><strong className="text-gray-800">Data Management</strong> — export and backup.</Bullet>
              </ul>
            </Card>
          </div>

          <Tip title="Getting Started">
            <p>
              Start with the SMTP configuration for basic email verification, then explore other
              settings based on your needs. Settings are saved when you click the save button.
            </p>
          </Tip>
        </div>
      )
    },
    {
      id: 'smtp',
      title: 'SMTP Configuration',
      icon: Mail,
      content: (
        <div className="space-y-5">
          <Card icon={Mail} title="SMTP Settings Explained">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Required Settings</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Host</strong> — SMTP server address (e.g., smtp.gmail.com)</li>
                  <li><strong className="text-gray-800">Port</strong> — SMTP port (587 for TLS, 465 for SSL)</li>
                  <li><strong className="text-gray-800">Username</strong> — your email address</li>
                  <li><strong className="text-gray-800">Password</strong> — app password or regular password</li>
                </ul>
              </div>
              <div>
                <Sub>Advanced Settings</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Secure</strong> — enable SSL/TLS encryption</li>
                  <li><strong className="text-gray-800">Timeout</strong> — connection timeout in milliseconds</li>
                  <li><strong className="text-gray-800">Concurrency</strong> — number of simultaneous connections</li>
                  <li><strong className="text-gray-800">Rate Limit</strong> — emails per minute limit</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Zap} title="Popular SMTP Providers">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Gmail</Sub>
                <ul className="space-y-1">
                  <li>Host: smtp.gmail.com</li>
                  <li>Port: 587 (TLS) or 465 (SSL)</li>
                  <li>Username: your-email@gmail.com</li>
                  <li>Password: app password required</li>
                </ul>
              </div>
              <div>
                <Sub>Outlook / Hotmail</Sub>
                <ul className="space-y-1">
                  <li>Host: smtp-mail.outlook.com</li>
                  <li>Port: 587 (TLS)</li>
                  <li>Username: your-email@outlook.com</li>
                  <li>Password: regular password</li>
                </ul>
              </div>
              <div>
                <Sub>Yahoo</Sub>
                <ul className="space-y-1">
                  <li>Host: smtp.mail.yahoo.com</li>
                  <li>Port: 587 (TLS) or 465 (SSL)</li>
                  <li>Username: your-email@yahoo.com</li>
                  <li>Password: app password required</li>
                </ul>
              </div>
              <div>
                <Sub>Custom Server</Sub>
                <ul className="space-y-1">
                  <li>Host: your SMTP server</li>
                  <li>Port: usually 25, 587, or 465</li>
                  <li>Username: your email or username</li>
                  <li>Password: your password</li>
                </ul>
              </div>
            </div>
          </Card>

          <Tip title="Pro Tips">
            <ul className="space-y-2">
              <Bullet><strong className="text-gray-800">Use App Passwords</strong> — for Gmail and Yahoo, generate app passwords instead of using your main password.</Bullet>
              <Bullet><strong className="text-gray-800">Test Connection</strong> — always test your SMTP settings before using them.</Bullet>
              <Bullet><strong className="text-gray-800">Rate Limits</strong> — start conservative (100 emails/minute) and adjust based on your provider.</Bullet>
              <Bullet><strong className="text-gray-800">Concurrency</strong> — higher concurrency is faster but may trigger spam filters.</Bullet>
            </ul>
          </Tip>
        </div>
      )
    },
    {
      id: 'imap',
      title: 'IMAP Configuration',
      icon: Mail,
      content: (
        <div className="space-y-5">
          <Card icon={Mail} title="IMAP Settings Explained">
            <p className="mb-4">
              IMAP (Internet Message Access Protocol) is used for advanced email verification.
              It allows the system to check whether emails actually exist by connecting to the mail
              server and inspecting the inbox.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Required Settings</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Host</strong> — IMAP server address</li>
                  <li><strong className="text-gray-800">Port</strong> — IMAP port (993 for SSL, 143 for non-SSL)</li>
                  <li><strong className="text-gray-800">Username</strong> — your email address</li>
                  <li><strong className="text-gray-800">Password</strong> — your email password</li>
                </ul>
              </div>
              <div>
                <Sub>Security Settings</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">TLS</strong> — enable for secure connections</li>
                  <li><strong className="text-gray-800">SSL</strong> — use SSL encryption</li>
                  <li><strong className="text-gray-800">Authentication</strong> — usually PLAIN or LOGIN</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Zap} title="Popular IMAP Providers">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Gmail</Sub>
                <ul className="space-y-1">
                  <li>Host: imap.gmail.com</li>
                  <li>Port: 993 (SSL)</li>
                  <li>TLS: enabled</li>
                  <li>Username: your-email@gmail.com</li>
                </ul>
              </div>
              <div>
                <Sub>Outlook / Hotmail</Sub>
                <ul className="space-y-1">
                  <li>Host: outlook.office365.com</li>
                  <li>Port: 993 (SSL)</li>
                  <li>TLS: enabled</li>
                  <li>Username: your-email@outlook.com</li>
                </ul>
              </div>
              <div>
                <Sub>Yahoo</Sub>
                <ul className="space-y-1">
                  <li>Host: imap.mail.yahoo.com</li>
                  <li>Port: 993 (SSL)</li>
                  <li>TLS: enabled</li>
                  <li>Username: your-email@yahoo.com</li>
                </ul>
              </div>
              <div>
                <Sub>Custom Server</Sub>
                <ul className="space-y-1">
                  <li>Host: your IMAP server</li>
                  <li>Port: usually 143 or 993</li>
                  <li>TLS: check with your provider</li>
                  <li>Username: your email or username</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Activity} title="When to Use IMAP">
            <ul className="space-y-2">
              <Bullet><strong className="text-gray-800">Advanced Verification</strong> — for more accurate email validation.</Bullet>
              <Bullet><strong className="text-gray-800">Business Emails</strong> — when verifying corporate email addresses.</Bullet>
              <Bullet><strong className="text-gray-800">High Accuracy Required</strong> — when you need the most reliable results.</Bullet>
              <Bullet><strong className="text-gray-800">Custom Domains</strong> — for verifying emails on your own domain.</Bullet>
            </ul>
          </Card>
        </div>
      )
    },
    {
      id: 'upload',
      title: 'Upload & Processing',
      icon: Upload,
      content: (
        <div className="space-y-5">
          <Card icon={Upload} title="Upload Settings Explained">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>File Limits</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Max Upload Size</strong> — maximum file size in MB</li>
                  <li><strong className="text-gray-800">Max Email List Size</strong> — maximum emails per file</li>
                  <li><strong className="text-gray-800">Supported Formats</strong> — CSV and TXT files</li>
                </ul>
              </div>
              <div>
                <Sub>Processing Settings</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Import Concurrency</strong> — files processed simultaneously</li>
                  <li><strong className="text-gray-800">Max Concurrent Uploads</strong> — uploads at once</li>
                  <li><strong className="text-gray-800">Worker Concurrency</strong> — background job workers</li>
                  <li><strong className="text-gray-800">SMTP Concurrency</strong> — email verification threads</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Zap} title="Performance Optimization">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Small Lists (1K–10K)</Sub>
                <ul className="space-y-1">
                  <li>Import concurrency: 2–4</li>
                  <li>SMTP concurrency: 5–10</li>
                  <li>Worker concurrency: 2</li>
                  <li>Expected speed: 100–500 emails/minute</li>
                </ul>
              </div>
              <div>
                <Sub>Large Lists (10K+)</Sub>
                <ul className="space-y-1">
                  <li>Import concurrency: 4–8</li>
                  <li>SMTP concurrency: 10–20</li>
                  <li>Worker concurrency: 4–8</li>
                  <li>Expected speed: 500–2000 emails/minute</li>
                </ul>
              </div>
            </div>
          </Card>

          <Tip title="Best Practices">
            <ul className="space-y-2">
              <Bullet><strong className="text-gray-800">Start Conservative</strong> — begin with lower concurrency and increase gradually.</Bullet>
              <Bullet><strong className="text-gray-800">Monitor Resources</strong> — watch CPU and memory usage.</Bullet>
              <Bullet><strong className="text-gray-800">Split Large Files</strong> — break files over 100K emails into smaller chunks.</Bullet>
              <Bullet><strong className="text-gray-800">Use Background Processing</strong> — large files are processed in the background.</Bullet>
              <Bullet><strong className="text-gray-800">Check SMTP Limits</strong> — do not exceed your provider's rate limits.</Bullet>
            </ul>
          </Tip>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Security & License',
      icon: Shield,
      content: (
        <div className="space-y-5">
          <Card icon={Shield} title="License Management">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>License Information</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">License Key</strong> — your purchase code</li>
                  <li><strong className="text-gray-800">Domain</strong> — registered domain</li>
                  <li><strong className="text-gray-800">IP Address</strong> — server IP address</li>
                  <li><strong className="text-gray-800">Status</strong> — active/inactive</li>
                </ul>
              </div>
              <div>
                <Sub>System Security</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">CORS Origin</strong> — allowed domains</li>
                  <li><strong className="text-gray-800">Log Level</strong> — debugging information</li>
                  <li><strong className="text-gray-800">API Tokens</strong> — secure API access</li>
                  <li><strong className="text-gray-800">Session Security</strong> — cookie settings</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Lock} title="Security Best Practices">
            <ul className="space-y-2">
              <Bullet><strong className="text-gray-800">Use HTTPS</strong> — always use SSL/TLS in production.</Bullet>
              <Bullet><strong className="text-gray-800">Strong Passwords</strong> — use complex passwords for all accounts.</Bullet>
              <Bullet><strong className="text-gray-800">App Passwords</strong> — use app-specific passwords for email services.</Bullet>
              <Bullet><strong className="text-gray-800">Regular Updates</strong> — keep the system updated.</Bullet>
              <Bullet><strong className="text-gray-800">Monitor Logs</strong> — check logs regularly for suspicious activity.</Bullet>
              <Bullet><strong className="text-gray-800">Backup Data</strong> — regular backups of verification results.</Bullet>
            </ul>
          </Card>

          <Card icon={Globe} title="CORS Configuration">
            <p className="mb-3">
              CORS (Cross-Origin Resource Sharing) controls which domains can access your API.
            </p>
            <ul className="space-y-1.5">
              <li><strong className="text-gray-800">Development</strong> — http://localhost:3000</li>
              <li><strong className="text-gray-800">Production</strong> — https://yourdomain.com</li>
              <li><strong className="text-gray-800">Multiple Domains</strong> — separate with commas</li>
              <li><strong className="text-gray-800">Wildcards</strong> — use * for all domains (not recommended for production)</li>
            </ul>
          </Card>
        </div>
      )
    },
    {
      id: 'templates',
      title: 'Templates & Data',
      icon: FileText,
      content: (
        <div className="space-y-5">
          <Card icon={FileText} title="Verification Templates">
            <p className="mb-4">
              Verification templates are custom email messages used during the verification process.
              They help determine whether an address is valid by attempting to send a test message.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Template Components</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Subject Line</strong> — email subject</li>
                  <li><strong className="text-gray-800">Message Body</strong> — email content (HTML supported)</li>
                  <li><strong className="text-gray-800">From Address</strong> — sender email</li>
                  <li><strong className="text-gray-800">Reply-To</strong> — reply address</li>
                </ul>
              </div>
              <div>
                <Sub>Best Practices</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Professional Tone</strong> — use business-like language</li>
                  <li><strong className="text-gray-800">Clear Purpose</strong> — explain why you are contacting them</li>
                  <li><strong className="text-gray-800">Unsubscribe Option</strong> — include an unsubscribe link</li>
                  <li><strong className="text-gray-800">Company Information</strong> — include your company details</li>
                </ul>
              </div>
            </div>
          </Card>

          <Card icon={Database} title="Error Strings & Bounce Management">
            <p className="mb-4">
              Error strings help identify bounced emails and categorize them properly.
              The system uses these patterns to determine if an email is invalid.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Sub>Common Bounce Types</Sub>
                <ul className="space-y-1.5">
                  <li><strong className="text-gray-800">Hard Bounce</strong> — email does not exist</li>
                  <li><strong className="text-gray-800">Soft Bounce</strong> — temporary delivery failure</li>
                  <li><strong className="text-gray-800">Blocked</strong> — server rejected the email</li>
                  <li><strong className="text-gray-800">Spam Filter</strong> — caught by spam filters</li>
                </ul>
              </div>
              <div>
                <Sub>Error String Examples</Sub>
                <ul className="space-y-1.5">
                  <li>"User not found"</li>
                  <li>"Mailbox does not exist"</li>
                  <li>"Recipient address rejected"</li>
                  <li>"550 5.1.1" (SMTP error codes)</li>
                </ul>
              </div>
            </div>
          </Card>

          <Tip title="Template Tips">
            <ul className="space-y-2">
              <Bullet><strong className="text-gray-800">Test Templates</strong> — always test templates before using them.</Bullet>
              <Bullet><strong className="text-gray-800">Keep It Simple</strong> — avoid complex HTML or images.</Bullet>
              <Bullet><strong className="text-gray-800">Include Contact Info</strong> — add your company contact details.</Bullet>
              <Bullet><strong className="text-gray-800">Comply with Laws</strong> — follow CAN-SPAM and GDPR requirements.</Bullet>
              <Bullet><strong className="text-gray-800">Monitor Bounces</strong> — regularly check bounce reports.</Bullet>
            </ul>
          </Tip>
        </div>
      )
    }
  ];

  const currentSectionData = sections.find(s => s.id === currentSection);

  return (
    <Portal>
      <div className="modal-overlay">
        <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full my-8 max-h-[calc(100vh-4rem)] overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 p-5 sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">Settings Guide</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Complete guide to configuring your email verifier</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close guide"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex">
            {/* Sidebar */}
            <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto max-h-[calc(100vh-11rem)] shrink-0">
              <nav className="p-4">
                <ul className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    const active = currentSection === section.id;
                    return (
                      <li key={section.id}>
                        <button
                          onClick={() => setCurrentSection(section.id)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center text-sm ${
                            active
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <Icon className={`w-4 h-4 mr-2.5 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                          {section.title}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto max-h-[calc(100vh-11rem)]">
              <div className="p-6">
                {currentSectionData?.content}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default SettingsGuide;
